import { MediaLayout } from 'modules/placements/types';
import styled, { css } from 'styled-components';
import { NeutralBanner } from 'ui/banner';
import { Tab } from 'ui/tabs';
import { WebLink, colors, fontSizes, fontWeights, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { Platform } from '../constants';

export const PreviewPaneContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  row-gap: ${spacing.plusHalf};
  max-width: ${pxToRem(1088)};

  [data-test='template-editor-embedded-preview'] {
    min-height: unset;
  }
`;

export const PreviewContent = styled.div`
  flex: 1 1 auto;
  padding-top: ${spacing.plusHalf};
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 ${spacing.plusHalf};
`;

const desktopDimensions = `
  width: 100%;
  aspect-ratio: 16 / 9;
`;

const mobileDimensions = `
  height: ${pxToRem(550)};
  width: ${pxToRem(300)};
`;

export const DevicePreview = styled.div<{ platform: Platform }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${({ platform }) => (platform === Platform.DESKTOP ? desktopDimensions : mobileDimensions)}
  overflow: hidden;

  font-size: ${fontSizes.s12};
  text-align: center;
  border-radius: ${pxToRem(20)};
  border: ${spacing.single} solid ${colors.grey25};
  background-color: ${colors.white};
`;

export const EmptyStateContainer = styled.div<{ platform: Platform }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 ${({ platform }) => (platform === Platform.DESKTOP ? pxToRem(170) : pxToRem(60))};
`;

export const EmptyStateText = styled.span`
  color: ${colors.textSecondary};
`;

export const StyledNeutralBanner = styled(NeutralBanner)`
  margin: ${pxToRem(22)} 0;
  padding-left: 0;
  padding-right: 0;
`;

export const MobileLayout = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;

  height: 100%;
  width: 100%;
`;

export const MobileTopBar = styled.div`
  height: ${spacing.double};
  background-color: ${colors.grey5};
  flex-shrink: 0;
`;

const HideScrollbar = css`
  ::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }
  -ms-overflow-style: none; /* Edge */
  scrollbar-width: none; /* Firefox */
`;

export const MobileContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.base};
  height: 100%;
  padding: ${spacing.base};
  margin-bottom: ${spacing.plusHalf};

  overflow-y: auto;
  ${HideScrollbar}
`;

export const MobileBottomBar = styled.div`
  height: ${pxToRem(56)};
  width: 100%;
  background-color: ${colors.grey5};
  border-top: ${spacing.single} solid ${colors.grey15};
  position: absolute;
  bottom: 0;
`;

export const DesktopLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.base};
  padding: ${spacing.base};
  height: 100%;
  width: 100%;

  overflow-y: auto;
  ${HideScrollbar}
`;

export const DesktopRow = styled.div`
  display: flex;
  gap: ${spacing.base};
  width: 100%;
`;

export const FillerRow = styled(DesktopRow)<{ mediaLayout?: MediaLayout }>`
  height: auto;
  flex-shrink: 1;
  flex-grow: ${({ mediaLayout }) => (mediaLayout === MediaLayout.TOP ? 3 : 7)};
`;

const DESKTOP_COLUMN_WIDTH = spacing.x10;

const DesktopResizing = (mediaLayout: MediaLayout) => `
  width: ${
    mediaLayout === MediaLayout.TOP
      ? DESKTOP_COLUMN_WIDTH
      : `calc(2 * ${DESKTOP_COLUMN_WIDTH} + ${spacing.base})`
  };
  
  #embedded-message-preview-title {
    font-size: ${spacing.half};
    line-height: ${spacing.threeQuarter};
  }

  #embedded-message-preview-body,
  #embedded-message-preview-action-buttons > a,
  #embedded-message-preview-action-buttons > span {
    font-size: ${spacing.threeEights};
    line-height: ${spacing.half};
    margin-top: 0;
  }

  #embedded-message-preview-action-buttons > button {
    padding: 0 ${pxToRem(5)};

    span {
      font-size: ${spacing.threeEights};
    }
  }
`;

export const MessageContainer = styled.div<{ mediaLayout: MediaLayout; platform: Platform }>`
  position: relative;
  border: ${spacing.single} solid ${colors.grey15};
  border-radius: ${spacing.threeEights};
  flex-shrink: 0;
  overflow: hidden;

  ${({ mediaLayout }) =>
    mediaLayout === MediaLayout.RIGHT &&
    css`
      min-height: ${pxToRem(80)};
    `}

  ${({ mediaLayout, platform }) => platform === Platform.DESKTOP && DesktopResizing(mediaLayout)}
`;

export const MessageContent = styled.div<{ mediaLayout: MediaLayout; platform: Platform }>`
  text-align: left;
  overflow-wrap: break-word;

  padding: ${({ platform }) =>
    platform === Platform.DESKTOP
      ? css`
          ${spacing.threeQuarter} ${spacing.half}
        `
      : css`
          ${spacing.base} ${spacing.threeQuarter}
        `};
`;

export const MessageText = styled.div<{ mediaLayout: MediaLayout }>`
  ${({ mediaLayout }) =>
    mediaLayout === MediaLayout.RIGHT &&
    css`
      width: ${spacing.x10};
      margin-top: ${spacing.half};
    `}
`;

export const Media = styled.div<{
  isVisible: boolean;
  platform: Platform;
  mediaLayout: MediaLayout;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: ${({ platform, mediaLayout }) => {
    if (mediaLayout === MediaLayout.TOP)
      return platform === Platform.DESKTOP ? spacing.x6 : spacing.x10;
    return platform === Platform.DESKTOP ? pxToRem(40) : spacing.x3;
  }};
  background-color: ${colors.grey5};

  img {
    height: 100%;
    width: 100%;
    object-fit: cover;
    ${({ isVisible }) =>
      !isVisible &&
      css`
        display: none;
      `}
  }

  ${({ mediaLayout, platform }) =>
    mediaLayout === MediaLayout.RIGHT &&
    css`
      position: absolute;
      top: ${spacing.base};
      right: ${spacing.threeQuarter};
      width: ${platform === Platform.DESKTOP ? pxToRem(40) : spacing.x3};
      border-radius: ${spacing.quarter};
      overflow: hidden;
    `}

  ${({ mediaLayout }) =>
    mediaLayout === MediaLayout.NONE &&
    css`
      display: none;
    `}
`;

export const Title = styled.div`
  color: ${colors.textPrimary};
  font-size: ${fontSizes.s13};
  font-weight: ${fontWeights.bold};
`;

export const Body = styled.div`
  color: ${colors.textSecondary};
  font-size: ${pxToRem(10)};
  line-height: ${spacing.base};
`;

export const ActionButtonsContainer = styled.div<{ mediaLayout: MediaLayout; platform: Platform }>`
  display: flex;
  gap: ${spacing.half};
  align-items: baseline;
  flex-wrap: wrap;

  margin-top: ${({ mediaLayout, platform }) => {
    if (platform === Platform.DESKTOP || mediaLayout === MediaLayout.NONE) return spacing.half;
    return spacing.base;
  }};
`;

export const ActionButtonText = styled.span`
  color: ${colors.textLink};
  font-size: ${pxToRem(10)};
  line-height: ${spacing.base};
  overflow-wrap: anywhere;
`;

export const ActionButtonLink = styled(WebLink)`
  font-size: ${pxToRem(10)};
`;

export const Filler = styled.div<{ maxHeight?: string; minHeight?: string; flexGrow?: number }>`
  background-color: ${colors.grey5};
  border-radius: ${spacing.threeEights};
  max-height: ${({ maxHeight }) => maxHeight};
  min-height: ${({ minHeight }) => minHeight};
  flex-shrink: 1;
  flex-grow: ${({ flexGrow }) => flexGrow ?? 1};
  width: 100%;
`;

export const StyledTab = styled(Tab)`
  display: flex;
  padding: ${spacing.quarter} ${spacing.half};
  color: ${colors.white};
  background-color: ${colors.purple100};
  pointer-events: none;

  span {
    font-size: ${pxToRem(10)};
  }
`;
